import {
  Box,
  Button,
  Icon,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  Theme,
  Typography,
} from "@outschool/backpack";
import { PaymentMethod } from "@outschool/gql-backend-generated";
import { CurrentUserPaymentMethodsQuery } from "@outschool/gql-frontend-generated";
import { faPlus } from "@outschool/icons";
import { Trans, useTranslation } from "@outschool/localization";
import { ApolloError } from "@outschool/ui-apollo";
import {
  Modal,
  ModalActionButtons,
} from "@outschool/ui-legacy-component-library";
import { pxToRem } from "@outschool/ui-utils";
import _ from "lodash";
import React from "react";

import * as Env from "../../../shared/Env";
import {
  useDeletePaymentMethod,
  usePaymentMethods,
} from "../../hooks/CurrentUser";
import ErrorMessage from "../ErrorMessage";
import LoadingWithMessage from "../LoadingWithMessage";
import { useDefaultStripeErrorMessage } from "./Stripe";

function RemovePaymentMethodModal({
  paymentMethod,
  open,
  onClose,
}: {
  paymentMethod: Pick<
    PaymentMethod,
    "uid" | "brand" | "last4" | "expirationMonth" | "expirationYear"
  >;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation(
    "client\\components\\payments\\StripeSavedCards",
  );
  const [
    deletePaymentMethod,
    { error: deletePaymentMethodError, loading: deletePaymentMethodLoading },
  ] = useDeletePaymentMethod();
  const errorMessage = useDefaultStripeErrorMessage();

  return (
    <Modal open={open} onClose={onClose}>
      <Typography variant="h3">
        {t("Remove credit card from account?")}
      </Typography>
      <Box
        sx={{
          marginBottom: "1em",
        }}
      >
        <Trans t={t}>
          Are you sure you want to remove{" "}
          <PaymentMethodLineItem paymentMethod={paymentMethod} /> from your
          account? Any active subscriptions will be canceled on Sunday unless
          you add another card to your profile before then.
        </Trans>
      </Box>
      {deletePaymentMethodError && (
        <ErrorMessage
          value={errorMessage}
          showStatusPageLink={Env.IS_READ_ONLY_MODE}
        />
      )}
      {deletePaymentMethodLoading ? (
        <LoadingWithMessage>
          {t`Please wait while we remove the credit card from your account.`}
        </LoadingWithMessage>
      ) : (
        <ModalActionButtons
          disabled={deletePaymentMethodLoading}
          primaryVariant="danger"
          primaryLabel={t`Remove card`}
          onPrimary={async () => {
            await deletePaymentMethod(paymentMethod.uid);
            onClose();
          }}
          onCancel={() => onClose()}
        />
      )}
    </Modal>
  );
}

export function PaymentMethodLineItem({
  paymentMethod,
}: {
  paymentMethod: Pick<
    PaymentMethod,
    "brand" | "last4" | "expirationMonth" | "expirationYear"
  >;
}) {
  const { t } = useTranslation(
    "client\\components\\payments\\StripeSavedCards",
  );
  return (
    <React.Fragment>
      {_.capitalize(paymentMethod.brand)} **** {paymentMethod.last4} {t("Exp.")}{" "}
      {/* eslint-disable-next-line i18next/no-literal-string */}
      {paymentMethod.expirationMonth.toFixed().padStart(2, "0")}/
      {paymentMethod.expirationYear.toString().substring(2)}
    </React.Fragment>
  );
}

interface SavedPaymentMethodSelectorProps {
  disabled: boolean;
  onFetchPaymentMethodsError: (error: ApolloError) => void;
  onSelectPaymentMethod: (
    paymentMethod:
      | NonNullable<
          CurrentUserPaymentMethodsQuery["currentUser"]
        >["paymentMethods"][number]
      | null,
  ) => void;
  onAddNewCard: () => void;
}

export const SavedPaymentMethodSelector: React.FC<
  SavedPaymentMethodSelectorProps
> = ({
  disabled,
  onSelectPaymentMethod,
  onAddNewCard,
  onFetchPaymentMethodsError,
}) => {
  const { t } = useTranslation(
    "client\\components\\payments\\StripeSavedCards",
  );
  const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState<
    | NonNullable<
        CurrentUserPaymentMethodsQuery["currentUser"]
      >["paymentMethods"][number]
    | null
  >(null);

  const {
    loading: paymentMethodsLoading,
    error: paymentMethodsError,
    paymentMethods = [],
  } = usePaymentMethods();
  const defaultPaymentMethod = paymentMethods.find(
    (
      paymentMethod: NonNullable<
        CurrentUserPaymentMethodsQuery["currentUser"]
      >["paymentMethods"][number],
    ) => paymentMethod.isCustomerDefault,
  );

  const handleSelectChange = (e: SelectChangeEvent<string>): void => {
    const selected = paymentMethods.find(pm => pm.uid === e.target.value);
    if (selected) {
      setSelectedPaymentMethod(selected);
      onSelectPaymentMethod(selected);
    }
  };

  React.useEffect(() => {
    if (!!defaultPaymentMethod) {
      onSelectPaymentMethod(defaultPaymentMethod);
      setSelectedPaymentMethod(defaultPaymentMethod);
    } else if (paymentMethods.length > 0) {
      onSelectPaymentMethod(paymentMethods[0]);
      setSelectedPaymentMethod(paymentMethods[0]);
    }
  }, [defaultPaymentMethod, paymentMethods, onSelectPaymentMethod]);

  React.useEffect(() => {
    if (!!paymentMethodsError) {
      onFetchPaymentMethodsError(paymentMethodsError);
    }
  }, [paymentMethodsError, onFetchPaymentMethodsError]);

  if (paymentMethodsLoading) {
    return (
      <Box>
        <Skeleton variant="text" width={150} height={24} sx={{ mb: 8 }} />
        <Skeleton
          variant="rectangular"
          width="100%"
          height={56}
          sx={{ mb: 8, borderRadius: 2 }}
        />
        <Skeleton
          variant="rectangular"
          width="100%"
          height={56}
          sx={{ mb: 28, borderRadius: 2 }}
        />
      </Box>
    );
  }

  return (
    <>
      {!!paymentMethods.length && selectedPaymentMethod && (
        <>
          <Box>
            <Typography
              variant="h6"
              mb={8}
              mr={4}
              sx={{ display: "inline-block" }}
            >
              {t("Saved Cards")}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "grey.600", fontSize: 14 }}
            >
              {t("(Powered by Stripe)")}
            </Typography>
          </Box>
          <Select
            disabled={disabled || paymentMethodsLoading}
            value={selectedPaymentMethod.uid}
            onChange={handleSelectChange}
            renderValue={() => {
              return (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Typography variant="body1">
                    <PaymentMethodLineItem
                      paymentMethod={selectedPaymentMethod}
                    />
                  </Typography>
                  {selectedPaymentMethod.isCustomerDefault && (
                    <Box
                      sx={{
                        backgroundColor: "primary.100",
                        color: "primary.700",
                        padding: "4px 8px",
                        borderRadius: 4,
                        fontSize: 12,
                        fontWeight: 600,
                        marginLeft: 8,
                      }}
                    >
                      {t("Default")}
                    </Box>
                  )}
                </Box>
              );
            }}
            fullWidth
            sx={{
              marginTop: 8,
              marginBottom: 8,
              backgroundColor: "white",
            }}
          >
            {paymentMethods.map(paymentMethod => (
              <MenuItem key={paymentMethod.uid} value={paymentMethod.uid}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Typography variant="body1">
                    <PaymentMethodLineItem paymentMethod={paymentMethod} />
                  </Typography>
                  {paymentMethod.isCustomerDefault && (
                    <Box
                      sx={{
                        backgroundColor: "primary.100",
                        color: "primary.700",
                        padding: "4px 8px",
                        borderRadius: 4,
                        fontSize: 12,
                        fontWeight: 600,
                        marginLeft: 8,
                      }}
                    >
                      {t("Default")}
                    </Box>
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
          <Box
            onClick={() => {
              if (disabled || paymentMethodsLoading) {
                return;
              }
              setSelectedPaymentMethod(null);
              onAddNewCard();
            }}
            sx={{
              border: "2px dashed",
              borderColor: "grey.300",
              borderRadius: 2,
              padding: "16px",
              marginBottom: 28,
              display: "flex",
              alignItems: "center",
              gap: 12,
              cursor: "pointer",
              "&:hover": {
                backgroundColor: "grey.50",
              },
            }}
          >
            <Typography
              variant="body1"
              sx={{ color: "primary.700", fontWeight: 500 }}
            >
              <Icon icon={faPlus} sx={{ marginRight: 8 }} />
              {t("Add a New Card")}
            </Typography>
          </Box>
        </>
      )}
    </>
  );
};

interface StripeEditSavedCardProps {
  defaultPaymentMethod: PaymentMethod;
  editCallback: () => void;
  disabled: boolean;
  replaceDescription?: string;
}

export function StripeEditSavedCard({
  defaultPaymentMethod,
  editCallback,
  disabled,
  replaceDescription,
}: StripeEditSavedCardProps) {
  const { t } = useTranslation(
    "client\\components\\payments\\StripeSavedCards",
  );

  return (
    <Box
      sx={{
        marginBottom: "2.5em",
      }}
    >
      <Box
        flex
        sx={{
          justifyContent: "space-between",
          padding: "0.75em 1em",
          border: "1px solid #CCC",
          borderRadius: "8px",
          marginBottom: "0.5em",
        }}
      >
        <PaymentMethodLineItem paymentMethod={defaultPaymentMethod} />
        {!disabled && (
          <Button variant="link" onClick={editCallback} disabled={disabled}>
            {t("Edit")}
          </Button>
        )}
      </Box>

      {!!replaceDescription && (
        <Box
          sx={{
            marginBottom: "1em",
            padding: "0.5em",
            borderRadius: pxToRem(8),
            backgroundColor: "grey.50",
            fontSize: "0.875em",
          }}
        >
          {replaceDescription}
        </Box>
      )}
    </Box>
  );
}

interface StripeReplaceOrTryAgainCardProps {
  defaultPaymentMethod: PaymentMethod;
  resumeYourSubscriptionsCallback: () => void;
  disabled: boolean;
}

export const StripeReplaceOrTryAgainCard = ({
  defaultPaymentMethod,
  resumeYourSubscriptionsCallback,
  disabled,
}: StripeReplaceOrTryAgainCardProps) => {
  const { t } = useTranslation(
    "client\\components\\payments\\StripeSavedCards",
  );

  const [deletePaymentMethod, { error, loading }] = useDeletePaymentMethod();
  const errorMessage = useDefaultStripeErrorMessage();

  const disableActions: boolean = loading || disabled || !!error;

  return (
    <Box>
      <Box
        flex
        sx={(theme: Theme) => ({
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          [theme.breakpoints.down("md")]: {
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 16,
          },
        })}
      >
        <Typography variant="body1">
          <PaymentMethodLineItem paymentMethod={defaultPaymentMethod} />
        </Typography>
        <Button
          variant="link"
          color="error"
          onClick={resumeYourSubscriptionsCallback}
          disabled={disableActions}
          sx={{
            lineHeight: 1.5,
          }}
        >
          {t("Try Again")}
        </Button>
        <Button
          variant="link"
          color="primary"
          onClick={async () => {
            // We delete the payment method immediately and skip the RemovePaymentMethodModal following new designs
            await deletePaymentMethod(defaultPaymentMethod.uid);
          }}
          disabled={disableActions}
          sx={{
            lineHeight: 1.5,
          }}
        >
          {t("Replace with New Card")}
        </Button>
      </Box>
      {error && (
        <ErrorMessage
          value={errorMessage}
          showStatusPageLink={Env.IS_READ_ONLY_MODE}
        />
      )}
    </Box>
  );
};

interface StripeRemoveSavedCardProps {
  defaultPaymentMethod: PaymentMethod;
  disabled: boolean;
}

export const StripeRemoveSavedCard = ({
  defaultPaymentMethod,
  disabled,
}: StripeRemoveSavedCardProps) => {
  const [showRemoveCardModal, setShowRemoveCardModal] = React.useState(false);
  const { t } = useTranslation(
    "client\\components\\payments\\StripeSavedCards",
  );

  return (
    <Box
      flex
      sx={{
        flexDirection: "row",
        gap: 16,
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Typography variant="body1">
        <PaymentMethodLineItem paymentMethod={defaultPaymentMethod} />
      </Typography>
      <Box>
        <Button
          variant="link"
          color="error"
          onClick={() => setShowRemoveCardModal(true)}
          disabled={disabled}
          sx={{
            lineHeight: 1.5,
          }}
        >
          {t("Remove")}
        </Button>
      </Box>
      <RemovePaymentMethodModal
        paymentMethod={defaultPaymentMethod}
        open={showRemoveCardModal}
        onClose={() => setShowRemoveCardModal(false)}
      />
    </Box>
  );
};
